import {
  faArrowRightFromBracket,
  faBoxHeart,
  faCalendarCheck,
  faCircleNodes,
  faDollarCircle,
  faGear,
  faMoon,
  faPeopleGroup,
  faSunBright,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouteLink } from 'react-router-dom';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { ToggleButton } from 'src/components/ToggleButton';
import { ToggleButtonGroup } from 'src/components/ToggleButtonGroup';
import { Typography } from 'src/components/Typography';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useOptionalSession, useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { signOutUser } from 'src/models/user/user';
import { getUserDisplayName, getUserInitials } from 'src/models/user/utils';
import toast from 'src/utils/toast';

interface UserDropdownDreamcoatProps {
  showV2Link?: boolean;
}

export const UserDropdownDreamcoat: React.FC<
  UserDropdownDreamcoatProps
> = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.labels' });
  const { t: tMessages } = useTranslation('translation', {
    keyPrefix: 'common.messages',
  });
  const { darkmodeDisabled, isDarkmode, setDarkmode, user } = useSession();
  const { refreshSessionAndUser } = useOptionalSession();

  const [open, setOpen] = useState(false);

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const onClickSetDarkMode = () => {
    setDarkmode(true);
  };
  const setLightMode = () => {
    setDarkmode(false);
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      await refreshSessionAndUser();
      toast.success(tMessages('You have been successfully signed out.'));
      window.location.href = externalLinks.login;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error signing out:', error);
      toast.error(
        tMessages('An error occurred while signing out. Please try again.')
      );
      // Optionally, you can show an error message to the user here
    }
  };

  const useDarkMode = isDarkmode();

  return (
    <DropdownMenu
      open={open}
      setOpen={setOpen}
      drawerOnMobile
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      trigger={
        !smDown ? (
          <Button
            variant="tertiary"
            sx={{
              '&&&&': {
                borderRadius: '50svh',
                px: '4px',
              },
            }}
          >
            <Avatar
              src={user?.profileImage}
              size="small"
              initials="DM"
              sx={{ mr: 0.5 }}
            >
              {getUserInitials(user)}
            </Avatar>
            {!smDown && (
              <Icon size="small" color="neutral" icon={faCaretDown} />
            )}
          </Button>
        ) : (
          <IconButton onClick={() => setOpen(!open)}>
            <Avatar src={user.profileImage} size="small">
              {getUserInitials(user)}
            </Avatar>
          </IconButton>
        )
      }
    >
      <ListItem
        primary={
          <Typography fontWeight={600}>{getUserDisplayName(user)}</Typography>
        }
        secondary={<Typography>{user.email}</Typography>}
        sx={{ py: 1.5 }}
      />
      <Divider sx={{ borderColor: 'neutral.borderRegular' }} />

      <Box py={1} sx={{ minWidth: 240 }}>
        <ListItem
          component={RouteLink}
          to={'/me/profile'}
          icon={faUser}
          primary={t('Profile')}
          clickable
        />
        <Divider sx={{ mx: 1 }} />
        <ListItem
          component={RouteLink}
          to={'/me/dependents'}
          icon={faPeopleGroup}
          primary={t('Dependents')}
          clickable
        />
        <Divider sx={{ mx: 1 }} />
        <ListItem
          component={RouteLink}
          to={'/me/packages'}
          icon={faBoxHeart}
          primary={t('My Packages')}
          clickable
        />
        <Divider sx={{ mx: 1 }} />
        <ListItem
          component={RouteLink}
          to={'/me/account'}
          icon={faDollarCircle}
          primary={t('Billing Information')}
          clickable
        />
        <Divider sx={{ mx: 1 }} />
        <ListItem
          component={RouteLink}
          to={'/me/visits'}
          icon={faCalendarCheck}
          primary={t('Visits')}
          clickable
        />
        <Divider sx={{ mx: 1 }} />
        <ListItem
          component={RouteLink}
          to={'/connections'}
          icon={faCircleNodes}
          primary={t('Apps & Devices')}
          clickable
        />
        <Divider sx={{ mx: 1 }} />
        <ListItem
          component={RouteLink}
          to={'/me/notifications'}
          icon={faGear}
          primary={t('Notification Settings')}
          clickable
        />

        <Divider sx={{ mx: 1 }} />

        {!darkmodeDisabled && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1, gap: 2 }}
          >
            <Typography variant="body1">{t('Appearance')}</Typography>
            <ToggleButtonGroup
              color="primary"
              exclusive
              onChange={(_, value) => {
                if (value === 'dark') {
                  onClickSetDarkMode();
                } else if (value === 'light') {
                  setLightMode();
                }
              }}
              value={useDarkMode ? 'dark' : 'light'}
            >
              <ToggleButton value="light">
                <Icon icon={faSunBright} size="small" />
              </ToggleButton>
              <ToggleButton value="dark">
                <Icon icon={faMoon} size="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}

        <Divider sx={{ mx: 1 }} />
        <ListItem
          onClick={handleSignOut}
          icon={faArrowRightFromBracket}
          primary={t('Log Out')}
          clickable
        />
      </Box>
    </DropdownMenu>
  );
};
